/**
 * Browser-side fetch implementation for API client.
 *
 * Calls the API directly with credentials: 'include' to send
 * cross-subdomain auth cookies (set with domain=.dev.toptoptime.com).
 */
import type { CustomFetchFn, CustomFetchOptions } from '@api';

// Type for error response
interface ErrorResponse {
  message?: string;
}

/**
 * Structured API error that carries the clean server-side message and HTTP status code.
 * Use `instanceof ApiError` in catch blocks to surface the message directly to the user.
 */
export class ApiError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

/**
 * Maximum retry attempts for 429 errors
 */
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Base delay for exponential backoff (milliseconds)
 */
const BASE_RETRY_DELAY_MS = 1000;

/**
 * Sleep helper for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Handle fetch response - parse JSON or text based on content type
 */
async function handleFetchResponse<T>(response: Response, _url: string, _options: CustomFetchOptions): Promise<T> {
  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as ErrorResponse;
    const errorMessage = errorData.message || response.statusText;
    throw new ApiError(errorMessage, response.status);
  }

  const contentType = response.headers.get('content-type') ?? '';
  return (contentType.includes('application/json') ? await response.json() : await response.text()) as T;
}

/**
 * Fetch with retry logic for 429 errors
 * Uses exponential backoff: 1s, 2s, 4s
 */
async function fetchWithRetry<T>(
  fetchFn: () => Promise<Response>,
  fullUrl: string,
  relativeUrl: string,
  options: CustomFetchOptions,
  retryCount: number = 0
): Promise<T> {
  let response: Response;
  try {
    response = await fetchFn();
  } catch (fetchError) {
    // AbortError is expected — React Query cancels in-flight requests
    // when components unmount or queries supersede each other. Don't
    // log it as a fetch failure; still propagate so callers see it.
    const isAbort =
      (fetchError instanceof DOMException && fetchError.name === 'AbortError') ||
      (fetchError instanceof Error && fetchError.name === 'AbortError');
    if (!isAbort) {
      const errorMessage = fetchError instanceof Error ? fetchError.message : String(fetchError);
      console.error(`[FETCH] [ERROR] ${options.method || 'GET'} ${fullUrl} - Fetch failed: ${errorMessage}`);
    }
    throw fetchError;
  }

  // Retry on 429 (rate limit) errors
  if (response.status === 429 && retryCount < MAX_RETRY_ATTEMPTS) {
    const delay = BASE_RETRY_DELAY_MS * Math.pow(2, retryCount);
    const retryAfter = response.headers.get('Retry-After');
    console.warn(
      `[FETCH] [429 RATE LIMITED] ${options.method || 'GET'} ${fullUrl} - Status: ${response.status} - Attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS}, waiting ${delay}ms${retryAfter ? ` (Retry-After: ${retryAfter})` : ''}`
    );
    await sleep(delay);
    return fetchWithRetry<T>(fetchFn, fullUrl, relativeUrl, options, retryCount + 1);
  }

  if (response.status === 429 && retryCount >= MAX_RETRY_ATTEMPTS) {
    console.error(
      `[FETCH] [429 EXHAUSTED] ${options.method || 'GET'} ${fullUrl} - Status: ${response.status} - All ${MAX_RETRY_ATTEMPTS} retries failed`
    );
  }

  return handleFetchResponse<T>(response, relativeUrl, options);
}

/**
 * Get the API URL from environment.
 * Falls back to window location for same-origin requests if not configured.
 */
export function getApiUrl(): string {
  // Use NEXT_PUBLIC_API_URL if available (set during build/runtime)
  if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }

  // Fallback: construct from current origin (for local dev without env var)
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    // Replace website domain with api subdomain
    // e.g., https://jordane.dev.toptoptime.com:8443 → https://api.jordane.dev.toptoptime.com
    return origin.replace(/^(https?:\/\/)/, '$1api.').replace(/:\d+$/, '');
  }

  throw new Error('API URL not configured and window not available');
}

/**
 * Browser-side custom fetch implementation.
 *
 * - Calls API directly with cross-subdomain cookies
 * - Uses credentials: 'include' to send httpOnly auth cookies
 * - Implements retry logic for 429 errors
 */
export const browserFetch: CustomFetchFn = async <T>(url: string, options: CustomFetchOptions): Promise<T> => {
  const apiUrl = getApiUrl();
  const fullUrl = `${apiUrl}${url}`;

  // Create headers object
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  };

  // Build fetch init options
  const init: RequestInit = {
    ...options,
    headers,
    // Include cookies for cross-origin requests to send httpOnly auth cookies
    credentials: 'include'
  };

  // Use fetchWithRetry for automatic 429 retry with exponential backoff
  return fetchWithRetry<T>(() => fetch(fullUrl, init), fullUrl, url, options);
};
